import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import InputTextPatrocinador from "../cadastro/inputtextPatrocinador";
import InputRadio from "../cadastro/inputradio";
import useApp from "../../hooks/useAppContext";
import useCadastro from "../../hooks/useCadastroContext";
import { useParams, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaSave,
  FaSpinner,
  FaIdCard,
  FaUserCog,
  FaKey,
  FaPaperPlane,
  FaCloudUploadAlt,
  FaImage,
  FaCrop,
  FaTrashAlt,
  FaExclamationTriangle,
  FaFileAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaCopy,
  FaLink,
  FaChevronDown,
  FaBan,
} from "react-icons/fa";
import "../cadastro/cadastro.css";
import "./admPatrocinadoresList.css";
import "./admFormPatrocinadores.css";
import "./admFormConvidados.css";
import DossieConvidadoSheet from "./DossieConvidadoSheet";
import AvatarCropModal from "../../components/avatar/AvatarCropModal";
import {
  cropAvatarCenterSquare,
  fetchAvatarViaApi,
} from "../../utils/cropAvatarImage";
import {
  ESTADOS_BR,
  REGIOES_BR,
  regiaoPorEstado,
} from "../../constants/estadosRegioes";
import api from "../../services/axios";
import { formatarDiaHora } from "../../services/conversaoTimer";
import {
  consultarTokenConteudos,
  gerarTokenConteudos,
} from "../credenciamentoConteudos/credenciamentoConteudosApi";

const FORM_TAB_IDENTIDADE = "identidade";
const FORM_TAB_TIPO = "tipo";
const FORM_TAB_SEGURANCA = "seguranca";
const FORM_TAB_EXCLUSAO = "exclusao";
const FORM_TAB_CONTEUDO = "conteudo";
const FORM_TAB_SLOTS = "slots";
const FORM_TAB_DOSSIE = "dossie";

function normalizarHorarioSlot(horario) {
  const raw = String(horario || "").trim();
  if (!raw) return "00:00:00";
  const partes = raw.split(":");
  const h = (partes[0] || "00").padStart(2, "0");
  const m = (partes[1] || "00").padStart(2, "0");
  const s = (partes[2] || "00").padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function toDatetimeLocalValue(isoOrSql) {
  if (!isoOrSql) return "";
  const d = new Date(isoOrSql);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function splitDatetimeParts(isoOrSql) {
  const local = toDatetimeLocalValue(isoOrSql);
  if (!local) return { data: "", hora: "" };
  const [data, hora] = local.split("T");
  return { data: data || "", hora: (hora || "").substring(0, 5) };
}

function montarDateTimeLocal(data, hora) {
  if (!data || !hora) return "";
  const horaNorm = String(hora).substring(0, 5);
  if (!/^\d{2}:\d{2}$/.test(horaNorm)) return "";
  return `${data}T${horaNorm}`;
}

function labelDiaEvento(item) {
  if (!item?.data) return item?.label || "";
  const [ano, mes, dia] = String(item.data).split("-");
  if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
  return item.label || item.data;
}

function slotInicioDate(slot) {
  const data = String(slot?.data || "").substring(0, 10);
  if (!data) return null;
  const horario = normalizarHorarioSlot(slot?.horario_inicio);
  const d = new Date(`${data}T${horario}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Bloqueia por chegada e/ou partida (cada uma já atualiza o preview sozinha). */
function calcularSlotsBloqueadosFront(slots, chegadaLocal, partidaLocal) {
  const chegada = chegadaLocal ? new Date(chegadaLocal) : null;
  const partida = partidaLocal ? new Date(partidaLocal) : null;
  const chegadaOk = chegada && !Number.isNaN(chegada.getTime());
  const partidaOk = partida && !Number.isNaN(partida.getTime());

  if (!chegadaOk && !partidaOk) return [];
  if (chegadaOk && partidaOk && partida <= chegada) return [];

  return (slots || [])
    .map((slot) => {
      const inicio = slotInicioDate(slot);
      if (!inicio) return null;
      let motivo = null;
      if (chegadaOk && inicio < chegada) motivo = "antes_chegada";
      else if (partidaOk && inicio >= partida) motivo = "apos_partida";
      if (!motivo) return null;
      return { ...slot, motivo };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const byData = String(a.data).localeCompare(String(b.data));
      if (byData !== 0) return byData;
      return String(a.horario_inicio).localeCompare(String(b.horario_inicio));
    });
}

function labelMotivoSlot(motivo) {
  if (motivo === "antes_chegada") return "Antes da chegada";
  if (motivo === "apos_partida") return "Após a partida";
  return "Bloqueado";
}

function formatarSlotResumo(slot) {
  const data = String(slot?.data || "").substring(0, 10);
  const hi = normalizarHorarioSlot(slot?.horario_inicio).substring(0, 5);
  const hf = normalizarHorarioSlot(slot?.horario_fim).substring(0, 5);
  const [y, m, d] = data.split("-");
  const dataBr = y && m && d ? `${d}/${m}/${y}` : data;
  return `${dataBr} · ${hi}–${hf}`;
}

function labelTipoConteudo(tipo) {
  if (tipo === "conteudo_patrocinado") return "Conteúdo patrocinado";
  if (tipo === "conteudo_editorial") return "Conteúdo editorial";
  if (tipo === "extraordinaria") return "Extraordinária";
  return tipo || "Conteúdo";
}

function toAgendaId(valor) {
  if (valor == null || valor === "" || valor === "null") return null;
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function labelOpcaoAgenda(item) {
  const nome = item?.nome || `Agenda #${item?.id}`;
  const raw = item?.dt_hora_inicio || item?.dtHoraInicio;
  if (!raw) return nome;
  return `${nome} — ${formatarDiaHora(raw).substring(0, 16)}`;
}

function normalizarAcaoEspecial(valor) {
  if (valor === true || valor === 1 || valor === "1" || valor === "true") {
    return true;
  }
  if (valor === false || valor === 0 || valor === "0" || valor === "false") {
    return false;
  }
  return null;
}

function labelAcaoEspecial(valor) {
  const normalizado = normalizarAcaoEspecial(valor);
  if (normalizado === true) return "Sim";
  if (normalizado === false) return "Não";
  return "Não informado";
}

const SEGMENTOS_DOSSIE = [
  ["", "Selecione uma opção"],
  ["operadora", "Operadora"],
  ["hospital", "Hospital"],
  ["clinica", "Clínica"],
  ["laboratorio", "Laboratório"],
  ["homecare", "Home care"],
  ["associacao", "Associação"],
  ["farmaceutica", "Farmacêutica"],
  ["outros", "Outros"],
];

const PROCURA_DOSSIE = [
  ["", "Selecione uma opção"],
  ["consultoria", "Consultoria"],
  ["contrucao_civil", "Construção civil"],
  ["consumiveis", "Consumíveis"],
  ["equipamentos", "Equipamentos"],
  ["facilities", "Facilities"],
  ["homecare", "Home care"],
  ["servicos", "Serviços"],
  ["tecnologia", "Tecnologia"],
  ["outros", "Outros"],
];

const FATURAMENTO_DOSSIE = [
  ["", "Selecione uma opção"],
  ["Abaixo de 1M", "Abaixo de 1M"],
  ["Entre 1M e 300M", "Entre 1M e 300M"],
  ["Entre 300M e 500M", "Entre 300M e 500M"],
  ["Entre 500M e 1B", "Entre 500M e 1B"],
  ["Entre 1B e 5B", "Entre 1B e 5B"],
  ["Superior a 5B", "Superior a 5B"],
];

const ATIVIDADES_INTERESSE_DOSSIE = [
  ["", "Selecione uma opção"],
  ["Gastronomia", "Gastronomia"],
  ["Esportes", "Esportes"],
  ["Cinema", "Cinema"],
  ["Viagens", "Viagens"],
  ["Leitura", "Leitura"],
  ["outros", "Outros"],
];

/** Converte hobby salvo (rótulo ou "Outros: …") para valor do select + texto. */
function parseHobbyForm(hobbySalvo) {
  const raw = String(hobbySalvo || "").trim();
  if (!raw) return { atividade: "", outros: "" };
  const outrosMatch = /^Outros:\s*(.*)$/i.exec(raw);
  if (outrosMatch) {
    return { atividade: "outros", outros: outrosMatch[1].trim() };
  }
  const conhecidas = ATIVIDADES_INTERESSE_DOSSIE.map(([v]) => v).filter(Boolean);
  if (conhecidas.includes(raw)) {
    return { atividade: raw, outros: "" };
  }
  return { atividade: "outros", outros: raw };
}

function formatHobbyForm(atividade, outros) {
  if (!atividade) return "";
  if (atividade === "outros") {
    const texto = String(outros || "").trim();
    return texto ? `Outros: ${texto}` : "";
  }
  return atividade;
}

/** Junta DDD + número para o input mascarado (evita duplicar se o telefone já tiver DDD). */
function telefoneComercialParaInput(dddComercial, telefoneComercial) {
  const ddd = String(dddComercial || "").replace(/\D/g, "");
  const tel = String(telefoneComercial || "").replace(/\D/g, "");
  if (!ddd && !tel) return "";
  if (!ddd) return tel;
  if (!tel) return ddd;
  if (tel.length >= 10) return tel.startsWith(ddd) ? tel : ddd + tel;
  if (tel.startsWith(ddd)) return tel;
  return ddd + tel;
}

const OPCOES_TIPO_USUARIO = [
  {
    value: "convidado",
    label: "Convidado",
    desc: "Participante do evento com perfil e agenda padrão.",
  },
  {
    value: "participante_patrocinador",
    label: "Participante patrocinador",
    desc: "Representante vinculado a um patrocinador.",
  },
  {
    value: "patrocinador",
    label: "Patrocinador",
    desc: "Acesso ao portal do patrocinador (conta da instituição). O vínculo com a ficha do patrocinador é feito em Patrocinadores.",
  },
  {
    value: "recepcao",
    label: "Recepção",
    desc: "Acesso às telas de recepção e check-in.",
  },
  {
    value: "administracao",
    label: "Administração",
    desc: "Acesso completo ao painel administrativo.",
  },
  {
    value: "administracao_dash",
    label: "Admin. dashboard",
    desc: "Acesso ao dashboard e indicadores.",
  },
];

const AdmFormConvidados = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getUsuario,
    handleImagemParticipante,
    redefinirSenha,
    excluirUsuario,
    getDiasEvento,
  } = useApp();
  const {
    nomeCompleto,
    setNomeCompleto,
    avatar,
    setAvatar,
    avatarOriginal,
    setAvatarOriginal,
    nomeCracha,
    setNomeCracha,
    bio,
    setBio,
    email,
    setEmail,
    cpf,
    setCPF,
    dataNascimento,
    setDataNascimento,
    telComercial,
    setTelComercial,
    setDdd,
    sexo,
    setSexo,
    celular,
    setCelular,
    nomeInstituicao,
    setnomeInstituicao,
    cargo,
    setCargo,
    showAlerta,
    salvarUsuario,
    tipo,
    setTipo,
    limpaVariaveis,
    estado,
    setEstado,
    regiao,
    setRegiao,
    segmento,
    setSegmento,
    segmentoOutros,
    setSegmentoOutros,
    faturamento,
    setfaturamento,
    procura,
    setprocura,
    outrosProcura,
    setOutrosProcura,
    hobby,
    setHobby,
    prioridade,
    setPrioridade,
    transformacao,
    setTransformacao,
  } = useCadastro();

  const [convidado, setConvidado] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRedefinindoSenha, setIsRedefinindoSenha] = useState(false);
  const [linkAlterarSenha, setLinkAlterarSenha] = useState("");
  const [showConfirmRedefinirSenha, setShowConfirmRedefinirSenha] =
    useState(false);
  const [showConfirmExcluirUsuario, setShowConfirmExcluirUsuario] =
    useState(false);
  const [isExcluindoUsuario, setIsExcluindoUsuario] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formTab, setFormTab] = useState(FORM_TAB_IDENTIDADE);
  const [preview, setPreview] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarDragActive, setAvatarDragActive] = useState(false);
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const [showAvatarCrop, setShowAvatarCrop] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const [hobbyAtividade, setHobbyAtividade] = useState("");
  const [hobbyOutros, setHobbyOutros] = useState("");
  const [tokenConteudo, setTokenConteudo] = useState(null);
  const [tokenConteudoLoading, setTokenConteudoLoading] = useState(false);
  const [tokenConteudoGerando, setTokenConteudoGerando] = useState(false);
  const [linkConteudoAberto, setLinkConteudoAberto] = useState(false);
  const [opcoesConteudo, setOpcoesConteudo] = useState([]);
  const [opcoesExtraordinaria, setOpcoesExtraordinaria] = useState([]);
  const [opcoesAgendaLoading, setOpcoesAgendaLoading] = useState(false);
  const [editConteudo1, setEditConteudo1] = useState("");
  const [editConteudo2, setEditConteudo2] = useState("");
  const [editExtraordinaria, setEditExtraordinaria] = useState("");
  const [salvandoAgendaConteudo, setSalvandoAgendaConteudo] = useState(false);
  const [slotsCatalogo, setSlotsCatalogo] = useState([]);
  const [slotsCatalogoLoading, setSlotsCatalogoLoading] = useState(false);
  const [diasEventoSlots, setDiasEventoSlots] = useState([]);
  const [diaChegada, setDiaChegada] = useState("");
  const [horaChegada, setHoraChegada] = useState("");
  const [diaPartida, setDiaPartida] = useState("");
  const [horaPartida, setHoraPartida] = useState("");
  const [slotsBloqueadosLoading, setSlotsBloqueadosLoading] = useState(false);
  const [salvandoSlotsBloqueados, setSalvandoSlotsBloqueados] = useState(false);
  const fileInputRef = useRef(null);
  const previewBlobRef = useRef(null);

  const titulo = id ? "Editar usuário" : "Novo usuário";
  const lead = id
    ? "Atualize os dados cadastrais, o tipo de acesso e a foto de perfil."
    : "Preencha os dados e escolha o tipo de usuário antes de salvar.";

  const carregaConvidado = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadConvidado = await getUsuario(id);
      const u = loadConvidado?.[0];
      if (!u) {
        toast.error("Usuário não encontrado.");
        return;
      }
      setConvidado(u);
      setLinkAlterarSenha("");
      setNomeCompleto(u.nome);
      setNomeCracha(u.nome_cracha);
      setEmail(u.email);
      setCPF(u.cpf);
      setDataNascimento(u?.dt_nascimento?.substring(0, 10) || "");
      const dddComercial = String(u.ddd_comercial || "").replace(/\D/g, "");
      const telefoneCompleto = telefoneComercialParaInput(
        dddComercial,
        u.telefone_comercial,
      );
      setDdd(dddComercial || telefoneCompleto.slice(0, 2));
      setTelComercial(telefoneCompleto);
      setSexo(u.sexo === "feminino" ? "Feminino" : "Masculino");
      const cel = String(u.celular || "").replace(/\D/g, "");
      setCelular(
        cel.startsWith("55") && cel.length >= 12 ? cel.slice(2) : cel,
      );
      setnomeInstituicao(u.empresa);
      setCargo(u.cargo);
      setTipo(u.tipo || "convidado");
      setAvatar(u.avatar);
      setAvatarUrl(u.avatar);
      setAvatarOriginal(null);
      setPreview(null);
      setBio(u.bio || "");
      setEstado(u.estado || "");
      setRegiao(u.regiao || "");
      setSegmento(u.tipo_instituicao || "");
      setSegmentoOutros(u.segmento_outros || "");
      setfaturamento(u.faturamento_instituicao || "");
      setprocura(u.procura_sbf || "");
      setOutrosProcura(u.outros_procura_sbf || "");
      setPrioridade(u.prioridade || "");
      setTransformacao(u.transformacao || "");
      const hobbyParsed = parseHobbyForm(u.hobby);
      setHobbyAtividade(hobbyParsed.atividade);
      setHobbyOutros(hobbyParsed.outros);
      setHobby(u.hobby || "");
      setEditConteudo1(toAgendaId(u.conteudo1) ? String(toAgendaId(u.conteudo1)) : "");
      setEditConteudo2(toAgendaId(u.conteudo2) ? String(toAgendaId(u.conteudo2)) : "");
      {
        let idExtra = toAgendaId(u.conteudo3);
        if (!idExtra) {
          const apExtra = (u.agendaParticipante || []).find(
            (ap) => ap?.agenda?.tipo === "extraordinaria",
          );
          idExtra = toAgendaId(
            apExtra?.agenda?.id || apExtra?.id_agenda || apExtra?.idAgenda,
          );
        }
        setEditExtraordinaria(idExtra ? String(idExtra) : "");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar usuário.");
    } finally {
      setIsLoading(false);
    }
  }, [
    id,
    getUsuario,
    setNomeCompleto,
    setNomeCracha,
    setEmail,
    setCPF,
    setDataNascimento,
    setTelComercial,
    setDdd,
    setSexo,
    setCelular,
    setnomeInstituicao,
    setCargo,
    setTipo,
    setAvatar,
    setAvatarOriginal,
    setBio,
    setEstado,
    setRegiao,
    setSegmento,
    setSegmentoOutros,
    setfaturamento,
    setprocura,
    setOutrosProcura,
    setPrioridade,
    setTransformacao,
    setHobby,
  ]);

  useEffect(() => {
    limpaVariaveis();
    setTipo("convidado");
    setFormTab(FORM_TAB_IDENTIDADE);
    setHobbyAtividade("");
    setHobbyOutros("");
    setTokenConteudo(null);
    if (id) {
      carregaConvidado();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const carregarTokenConteudo = useCallback(async () => {
    if (!id) {
      setTokenConteudo(null);
      return;
    }
    setTokenConteudoLoading(true);
    try {
      const data = await consultarTokenConteudos(id);
      setTokenConteudo(data?.dados ?? null);
    } catch (err) {
      console.error(err);
      setTokenConteudo(null);
    } finally {
      setTokenConteudoLoading(false);
    }
  }, [id]);

  const carregarOpcoesAgendaConteudo = useCallback(async () => {
    setOpcoesAgendaLoading(true);
    try {
      const [respConteudos, respExtra] = await Promise.all([
        api.post("/agenda", {
          tipo: ["conteudo_editorial", "conteudo_patrocinado"],
          adm: true,
        }),
        api.post("/agenda", {
          tipo: ["extraordinaria"],
          adm: true,
        }),
      ]);
      setOpcoesConteudo(
        Array.isArray(respConteudos?.data?.dados) ? respConteudos.data.dados : [],
      );
      setOpcoesExtraordinaria(
        Array.isArray(respExtra?.data?.dados) ? respExtra.data.dados : [],
      );
    } catch (err) {
      console.error(err);
      setOpcoesConteudo([]);
      setOpcoesExtraordinaria([]);
      toast.error("Erro ao carregar opções de agenda.");
    } finally {
      setOpcoesAgendaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formTab === FORM_TAB_CONTEUDO && id) {
      carregarTokenConteudo();
      carregarOpcoesAgendaConteudo();
    }
  }, [formTab, id, carregarTokenConteudo, carregarOpcoesAgendaConteudo]);

  const carregarAbaSlots = useCallback(async () => {
    if (!id) return;
    setSlotsBloqueadosLoading(true);
    setSlotsCatalogoLoading(true);
    try {
      const [slotsResp, diasDados, bloqueadosResp] = await Promise.all([
        api.get("/slots"),
        getDiasEvento(),
        api.get(`/usuarios-slots-bloqueados/${id}`),
      ]);

      const dias = Array.isArray(diasDados?.dias) ? diasDados.dias : [];
      setDiasEventoSlots(dias);
      setSlotsCatalogo(
        Array.isArray(slotsResp?.data?.dados) ? slotsResp.data.dados : [],
      );

      const dados = bloqueadosResp?.data?.dados;
      const chegada = splitDatetimeParts(dados?.data_hora_chegada);
      const partida = splitDatetimeParts(dados?.data_hora_partida);
      const primeiroDia = dias[0]?.data || "";
      const ultimoDia = dias[dias.length - 1]?.data || "";

      setDiaChegada(chegada.data || primeiroDia);
      setHoraChegada(chegada.hora || "08:00");
      setDiaPartida(partida.data || ultimoDia);
      setHoraPartida(partida.hora || "23:00");
    } catch (err) {
      console.error(err);
      setSlotsCatalogo([]);
      setDiasEventoSlots([]);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar disponibilidade de slots.",
      );
    } finally {
      setSlotsBloqueadosLoading(false);
      setSlotsCatalogoLoading(false);
    }
  }, [id, getDiasEvento]);

  useEffect(() => {
    if (formTab === FORM_TAB_SLOTS && id) {
      carregarAbaSlots();
    }
  }, [formTab, id, carregarAbaSlots]);

  const dataHoraChegada = useMemo(
    () => montarDateTimeLocal(diaChegada, horaChegada),
    [diaChegada, horaChegada],
  );
  const dataHoraPartida = useMemo(
    () => montarDateTimeLocal(diaPartida, horaPartida),
    [diaPartida, horaPartida],
  );
  const temPreviewSlots = Boolean(dataHoraChegada || dataHoraPartida);

  const slotsBloqueadosPreview = useMemo(
    () =>
      calcularSlotsBloqueadosFront(
        slotsCatalogo,
        dataHoraChegada,
        dataHoraPartida,
      ),
    [slotsCatalogo, dataHoraChegada, dataHoraPartida],
  );

  const slotsDisponiveisPreview = useMemo(() => {
    if (!temPreviewSlots) return [];
    const idsBloqueados = new Set(slotsBloqueadosPreview.map((s) => s.id));
    return (slotsCatalogo || []).filter((s) => !idsBloqueados.has(s.id));
  }, [slotsCatalogo, slotsBloqueadosPreview, temPreviewSlots]);

  const handleSalvarSlotsBloqueados = async () => {
    if (!id) return;
    if (!diaChegada || !horaChegada || !diaPartida || !horaPartida) {
      toast.error("Informe dia e hora de chegada e de partida.");
      return;
    }
    if (
      !diasEventoSlots.some((d) => d.data === diaChegada) ||
      !diasEventoSlots.some((d) => d.data === diaPartida)
    ) {
      toast.error("Selecione apenas dias do evento vigente.");
      return;
    }
    if (!dataHoraChegada || !dataHoraPartida) {
      toast.error("Informe data/hora de chegada e de partida.");
      return;
    }
    const chegada = new Date(dataHoraChegada);
    const partida = new Date(dataHoraPartida);
    if (Number.isNaN(chegada.getTime()) || Number.isNaN(partida.getTime())) {
      toast.error("Datas inválidas.");
      return;
    }
    if (partida <= chegada) {
      toast.error("A partida deve ser posterior à chegada.");
      return;
    }

    setSalvandoSlotsBloqueados(true);
    try {
      const resp = await api.post("/usuarios-slots-bloqueados", {
        id_usuario: Number(id),
        data_hora_chegada: dataHoraChegada,
        data_hora_partida: dataHoraPartida,
      });
      if (resp?.data?.ok === false) {
        toast.error(
          resp.data.mensagem || "Não foi possível salvar os slots bloqueados.",
        );
        return;
      }
      toast.success(
        resp?.data?.mensagem || "Slots bloqueados registrados.",
      );
      const dados = resp?.data?.dados;
      if (dados) {
        const chegadaParts = splitDatetimeParts(dados.data_hora_chegada);
        const partidaParts = splitDatetimeParts(dados.data_hora_partida);
        setDiaChegada(chegadaParts.data);
        setHoraChegada(chegadaParts.hora);
        setDiaPartida(partidaParts.data);
        setHoraPartida(partidaParts.hora);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao salvar slots bloqueados do usuário.",
      );
    } finally {
      setSalvandoSlotsBloqueados(false);
    }
  };

  const handleSalvarAgendaConteudo = async () => {
    if (!id) return;

    const c1 = toAgendaId(editConteudo1);
    const c2 = toAgendaId(editConteudo2);
    if (c1 && c2 && c1 === c2) {
      toast.error("Selecione conteúdos diferentes para os slots 1 e 2.");
      return;
    }

    setSalvandoAgendaConteudo(true);
    try {
      const resp = await api.post("/agenda-convidado-conteudos", {
        id_usuario: Number(id),
        conteudo1: c1,
        conteudo2: c2,
        extraordinaria: toAgendaId(editExtraordinaria),
        acao_especial: normalizarAcaoEspecial(
          convidado?.acao_especial ?? convidado?.acaoEspecial,
        ),
      });
      if (resp?.data?.ok === false) {
        toast.error(resp.data.mensagem || "Não foi possível salvar a agenda.");
        return;
      }
      toast.success(resp?.data?.mensagem || "Agenda atualizada.");
      await carregaConvidado();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao salvar agenda de conteúdos.",
      );
    } finally {
      setSalvandoAgendaConteudo(false);
    }
  };

  const handleGerarTokenConteudo = async () => {
    if (!id) return;
    setTokenConteudoGerando(true);
    try {
      const data = await gerarTokenConteudos(id);
      if (data?.dados) {
        setTokenConteudo(data.dados);
        toast.success(data.mensagem || "Link gerado.");
      } else {
        toast.error("Não foi possível gerar o link.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao gerar link de conteúdos.",
      );
    } finally {
      setTokenConteudoGerando(false);
    }
  };

  const handleCopiarLinkConteudo = async () => {
    const url = tokenConteudo?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const labelStatusTokenConteudo = (status) => {
    if (status === "ativo") return "Ativo";
    if (status === "usado") return "Usado";
    if (status === "expirado") return "Expirado";
    return "Inexistente";
  };

  const atualizarHobby = (atividade, outros) => {
    setHobbyAtividade(atividade);
    setHobbyOutros(outros);
    setHobby(formatHobbyForm(atividade, outros));
  };

  useEffect(() => {
    return () => {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, []);

  const applyCroppedAvatar = useCallback(
    (cropped, originalFile = null) => {
      if (!cropped) return;
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
      }
      const url = URL.createObjectURL(cropped);
      previewBlobRef.current = url;
      setPreview(url);
      setAvatar(cropped);
      if (originalFile instanceof File || originalFile instanceof Blob) {
        setAvatarOriginal(originalFile);
      }
    },
    [setAvatar, setAvatarOriginal],
  );

  const closeAvatarCrop = useCallback(() => {
    setShowAvatarCrop(false);
    setAvatarCropFile(null);
  }, []);

  const handleAvatarCropConfirm = useCallback(
    (cropped, err) => {
      if (err || !cropped) {
        toast.error("Não foi possível processar a imagem.");
        return;
      }
      const original =
        avatarCropFile instanceof File || avatarCropFile instanceof Blob
          ? avatarCropFile
          : avatarOriginal instanceof File || avatarOriginal instanceof Blob
            ? avatarOriginal
            : null;
      applyCroppedAvatar(cropped, original);
      closeAvatarCrop();
      toast.success("Recorte aplicado. Salve o usuário para substituir o avatar.");
    },
    [applyCroppedAvatar, closeAvatarCrop, avatarCropFile, avatarOriginal],
  );

  const openAvatarCropExisting = useCallback(async () => {
    if (!id) return;
    const temFonteRemota = Boolean(
      avatarUrl ||
        (convidado?.avatar && convidado.avatar !== "") ||
        (typeof avatar === "string" && avatar !== ""),
    );
    const temOriginalLocalPendente =
      (avatarOriginal instanceof File || avatarOriginal instanceof Blob) &&
      (avatar instanceof File || avatar instanceof Blob);

    if (
      !temFonteRemota &&
      !temOriginalLocalPendente &&
      !(avatar instanceof File) &&
      !(avatar instanceof Blob)
    ) {
      toast.error("Este convidado ainda não possui foto para ajustar.");
      return;
    }

    setAvatarProcessing(true);
    try {
      let file = null;
      // 1) Original local de um upload ainda não persistido
      // 2) Senão, `_original` no S3 (com fallback de extensões no backend)
      if (temOriginalLocalPendente) {
        file =
          avatarOriginal instanceof File
            ? avatarOriginal
            : new File([avatarOriginal], "avatar_original.jpg", {
                type: avatarOriginal.type || "image/jpeg",
                lastModified: Date.now(),
              });
      } else if (temFonteRemota) {
        file = await fetchAvatarViaApi(api, id, { original: true });
        setAvatarOriginal(file);
      } else if (avatar instanceof File) {
        file = avatar;
      } else if (avatar instanceof Blob) {
        file = new File([avatar], "avatar.jpg", {
          type: avatar.type || "image/jpeg",
          lastModified: Date.now(),
        });
      }
      setAvatarCropFile(file);
      setShowAvatarCrop(true);
    } catch (err) {
      console.error(err);
      toast.error(
        "Não foi possível carregar a foto atual para edição. Tente enviar a imagem novamente.",
      );
    } finally {
      setAvatarProcessing(false);
    }
  }, [id, avatarUrl, convidado, avatar, avatarOriginal, setAvatarOriginal]);

  const applyAvatarFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Use um arquivo de imagem (PNG, JPG, WebP, etc.).");
        return;
      }
      const maxBytes = 15 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error("Imagem muito grande. Tamanho máximo: 15 MB.");
        return;
      }

      // Na edição do convidado pelo admin: modal interativo (zoom + arraste).
      if (id) {
        setAvatarOriginal(file);
        setAvatarCropFile(file);
        setShowAvatarCrop(true);
        return;
      }

      setAvatarProcessing(true);
      try {
        const cropped = await cropAvatarCenterSquare(file);
        applyCroppedAvatar(cropped, file);
      } catch (err) {
        console.error(err);
        toast.error("Não foi possível processar a imagem.");
      } finally {
        setAvatarProcessing(false);
      }
    },
    [applyCroppedAvatar, id, setAvatarOriginal],
  );

  const handleAvatarInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) applyAvatarFile(f);
    e.target.value = "";
  };

  const openAvatarPicker = () => fileInputRef.current?.click();

  const onAvatarDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragActive(true);
  };

  const onAvatarDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setAvatarDragActive(false);
    }
  };

  const onAvatarDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onAvatarDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) applyAvatarFile(f);
  };

  const onAvatarZoneKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAvatarPicker();
    }
  };

  const validarFormulario = () => {
    const obrigatorios = [
      [nomeCompleto, "Nome completo"],
      [nomeCracha, "Nome para o crachá"],
      [email, "E-mail"],
      [cpf, "CPF"],
      [dataNascimento, "Data de nascimento"],
      [telComercial, "Telefone comercial"],
      [celular, "Celular"],
      [nomeInstituicao, "Nome da instituição"],
      [cargo, "Cargo"],
    ];
    for (const [valor, rotulo] of obrigatorios) {
      if (!String(valor || "").trim()) {
        toast.error(`Preencha: ${rotulo}.`);
        setFormTab(FORM_TAB_IDENTIDADE);
        return false;
      }
    }
    if (!sexo) {
      toast.error("Selecione o sexo.");
      setFormTab(FORM_TAB_IDENTIDADE);
      return false;
    }
    if (!tipo) {
      toast.error("Selecione o tipo de usuário.");
      setFormTab(FORM_TAB_TIPO);
      return false;
    }
    return true;
  };

  const handleSalvar = async () => {
    if (!validarFormulario()) return;
    setIsSaving(true);
    try {
      const resp = await salvarUsuario(id || undefined);
      // Erros da API também trazem `mensagem`; só recarrega em sucesso (sem `erro`).
      if (resp?.erro) {
        toast.error(resp.mensagem || "Não foi possível salvar o usuário.");
        if (
          String(resp.erro).includes("estado") ||
          String(resp.erro).includes("regiao")
        ) {
          setFormTab(FORM_TAB_IDENTIDADE);
        }
        return;
      }
      if (resp?.mensagem) {
        toast.success(resp.mensagem);
        const novoId = resp?.dados?.novoUsuarioInserido?.id;
        if (!id && novoId) {
          navigate(`/private/adm/convidados/editar/${novoId}`);
        } else if (id) {
          await carregaConvidado();
        }
      } else {
        toast.error("Não foi possível salvar o usuário.");
      }
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      const msg = data?.mensagem || "Erro ao salvar usuário.";
      toast.error(msg);
      if (
        data?.erro &&
        (String(data.erro).includes("estado") ||
          String(data.erro).includes("regiao"))
      ) {
        setFormTab(FORM_TAB_IDENTIDADE);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const abrirConfirmacaoRedefinirSenha = () => {
    if (!id || !email) return;
    setShowConfirmRedefinirSenha(true);
  };

  const confirmarRedefinirSenha = async () => {
    if (!id) return;
    setShowConfirmRedefinirSenha(false);
    setIsRedefinindoSenha(true);
    try {
      const resp = await redefinirSenha(id);
      if (resp?.mensagem) {
        const url =
          resp?.dados?.url_alterar_senha ||
          resp?.dados?.urlAlterarSenha ||
          "";
        if (url) {
          setLinkAlterarSenha(url);
        }
        toast.success(resp.mensagem);
      } else {
        toast.error(
          resp?.mensagem || "Não foi possível enfileirar a redefinição de senha.",
        );
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.mensagem ||
        "Erro ao solicitar redefinição de senha.";
      toast.error(msg);
    } finally {
      setIsRedefinindoSenha(false);
    }
  };

  const handleCopiarLinkAlterarSenha = async () => {
    if (!linkAlterarSenha) return;
    try {
      await navigator.clipboard.writeText(linkAlterarSenha);
      toast.success("Link copiado.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const abrirConfirmacaoExcluirUsuario = () => {
    if (!id) return;
    setShowConfirmExcluirUsuario(true);
  };

  const confirmarExcluirUsuario = async () => {
    if (!id) return;
    setShowConfirmExcluirUsuario(false);
    setIsExcluindoUsuario(true);
    try {
      const resp = await excluirUsuario(id);
      if (resp?.mensagem) {
        toast.success(resp.mensagem);
        navigate(-1);
      } else {
        toast.error(resp?.mensagem || "Não foi possível excluir o usuário.");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.mensagem || "Erro ao excluir usuário.";
      toast.error(msg);
    } finally {
      setIsExcluindoUsuario(false);
    }
  };

  const tipoSelecionado = OPCOES_TIPO_USUARIO.find((o) => o.value === tipo);
  const avatarExibir =
    preview || avatarUrl || handleImagemParticipante(convidado);
  const temAvatarReal = Boolean(
    preview ||
      avatarUrl ||
      (convidado?.avatar && convidado.avatar !== "") ||
      avatar instanceof File ||
      avatar instanceof Blob,
  );

  const opcoesSegmento = [...SEGMENTOS_DOSSIE];
  if (
    segmento &&
    !SEGMENTOS_DOSSIE.some(([v]) => v === segmento)
  ) {
    opcoesSegmento.push([segmento, segmento]);
  }
  const opcoesProcura = [...PROCURA_DOSSIE];
  if (procura && !PROCURA_DOSSIE.some(([v]) => v === procura)) {
    opcoesProcura.push([procura, procura]);
  }
  const opcoesFaturamento = [...FATURAMENTO_DOSSIE];
  if (
    faturamento &&
    !FATURAMENTO_DOSSIE.some(([v]) => v === faturamento)
  ) {
    opcoesFaturamento.push([faturamento, faturamento]);
  }
  const opcoesHobby = [...ATIVIDADES_INTERESSE_DOSSIE];
  if (
    hobbyAtividade &&
    hobbyAtividade !== "outros" &&
    !ATIVIDADES_INTERESSE_DOSSIE.some(([v]) => v === hobbyAtividade)
  ) {
    opcoesHobby.push([hobbyAtividade, hobbyAtividade]);
  }

  const convidadoDossiePreview = convidado
    ? {
        ...convidado,
        nome: nomeCompleto || convidado.nome,
        nome_cracha: nomeCracha || convidado.nome_cracha,
        empresa: nomeInstituicao || convidado.empresa,
        cargo: cargo || convidado.cargo,
        bio: bio ?? convidado.bio,
        estado: estado || convidado.estado,
        regiao: regiao || convidado.regiao,
        tipo_instituicao: segmento || convidado.tipo_instituicao,
        segmento_outros: segmentoOutros || convidado.segmento_outros,
        faturamento_instituicao:
          faturamento || convidado.faturamento_instituicao,
        prioridade: prioridade || convidado.prioridade,
        transformacao: transformacao || convidado.transformacao,
        procura_sbf: procura || convidado.procura_sbf,
        outros_procura_sbf: outrosProcura || convidado.outros_procura_sbf,
        hobby: hobby || convidado.hobby,
        avatar: avatarExibir || convidado.avatar,
      }
    : null;

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header" aria-label="Cabeçalho do formulário">
          <div className="apl-header-text">
            <h1>{titulo}</h1>
            <p>{lead}</p>
          </div>
          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-toolbar-actions">
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={handleSalvar}
                disabled={isSaving || isLoading}
              >
                {isSaving ? (
                  <FaSpinner className="fa-spin" size={14} aria-hidden />
                ) : (
                  <FaSave size={14} aria-hidden />
                )}
                {isSaving ? "Salvando…" : "Salvar"}
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        <AvatarCropModal
          show={showAvatarCrop}
          imageFile={avatarCropFile}
          originalFile={
            avatarOriginal instanceof File || avatarOriginal instanceof Blob
              ? avatarOriginal
              : null
          }
          onReloadOriginal={async () => {
            const temOriginalLocalPendente =
              (avatarOriginal instanceof File ||
                avatarOriginal instanceof Blob) &&
              (avatar instanceof File || avatar instanceof Blob);
            // Upload novo ainda não salvo: mantém a original em memória
            if (temOriginalLocalPendente) {
              if (avatarOriginal instanceof File) return avatarOriginal;
              return new File([avatarOriginal], "avatar_original.jpg", {
                type: avatarOriginal.type || "image/jpeg",
                lastModified: Date.now(),
              });
            }
            // Avatar já persistido: busca `_original` no S3 via API
            if (id) {
              const file = await fetchAvatarViaApi(api, id, {
                original: true,
              });
              setAvatarOriginal(file);
              setAvatarCropFile(file);
              return file;
            }
            return avatarCropFile;
          }}
          onCancel={closeAvatarCrop}
          onConfirm={handleAvatarCropConfirm}
        />

        <Modal
          show={showConfirmRedefinirSenha}
          onHide={() => setShowConfirmRedefinirSenha(false)}
          centered
          className="acu-redefinir-senha-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Redefinir senha</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="acu-redefinir-senha-modal-text mb-0">
              Será enviado um e-mail para{" "}
              <strong>{email || "o usuário"}</strong> com um link para definir
              uma nova senha. O link expira em <strong>48 horas</strong> e a
              senha atual não será alterada até o usuário concluir o fluxo.
            </p>
            <p className="acu-redefinir-senha-modal-text mb-0 mt-3">
              Deseja continuar?
            </p>
          </Modal.Body>
          <Modal.Footer className="acu-redefinir-senha-modal-footer">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => setShowConfirmRedefinirSenha(false)}
              disabled={isRedefinindoSenha}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={confirmarRedefinirSenha}
              disabled={isRedefinindoSenha}
            >
              {isRedefinindoSenha ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaPaperPlane size={14} aria-hidden />
              )}
              {isRedefinindoSenha ? "Enviando…" : "Confirmar envio"}
            </button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showConfirmExcluirUsuario}
          onHide={() => !isExcluindoUsuario && setShowConfirmExcluirUsuario(false)}
          centered
          className="acu-excluir-usuario-modal"
          backdrop={isExcluindoUsuario ? "static" : true}
        >
          <Modal.Header closeButton={!isExcluindoUsuario}>
            <Modal.Title className="acu-excluir-usuario-modal-title">
              <FaExclamationTriangle
                size={18}
                className="acu-excluir-usuario-modal-icon"
                aria-hidden
              />
              Confirmar exclusão
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="acu-excluir-usuario-modal-text mb-0">
              Você está prestes a excluir o usuário{" "}
              <strong>{nomeCompleto || "sem nome"}</strong>
              {email ? (
                <>
                  {" "}
                  (<strong>{email}</strong>)
                </>
              ) : null}
              .
            </p>
            <p className="acu-excluir-usuario-modal-text acu-excluir-usuario-modal-text--danger mb-0 mt-3">
              Esta ação é <strong>definitiva</strong>. O usuário deixará de
              aparecer no sistema, perderá acesso ao portal e não poderá ser
              recuperado pela interface administrativa.
            </p>
            <p className="acu-excluir-usuario-modal-text mb-0 mt-3">
              Deseja confirmar a exclusão?
            </p>
          </Modal.Body>
          <Modal.Footer className="acu-excluir-usuario-modal-footer">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => setShowConfirmExcluirUsuario(false)}
              disabled={isExcluindoUsuario}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="apl-btn acu-btn--danger"
              onClick={confirmarExcluirUsuario}
              disabled={isExcluindoUsuario}
            >
              {isExcluindoUsuario ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaTrashAlt size={14} aria-hidden />
              )}
              {isExcluindoUsuario ? "Excluindo…" : "Excluir definitivamente"}
            </button>
          </Modal.Footer>
        </Modal>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          <section
            className="apl-card apf-form-card"
            aria-label="Formulário de usuário"
          >
            {isLoading ? (
              <div className="apl-loading" aria-live="polite">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando usuário…</span>
              </div>
            ) : (
              <>
                <div className="apf-form-tabs-shell">
                  <nav
                    className="apf-form-tabs-nav"
                    role="tablist"
                    aria-label="Seções do formulário"
                  >
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-identidade"
                      aria-selected={formTab === FORM_TAB_IDENTIDADE}
                      aria-controls="acu-panel-identidade"
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_IDENTIDADE
                          ? "apf-form-tab--active"
                          : ""
                      }`}
                      onClick={() => setFormTab(FORM_TAB_IDENTIDADE)}
                    >
                      <FaIdCard
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Identidade e contato
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-tipo"
                      aria-selected={formTab === FORM_TAB_TIPO}
                      aria-controls="acu-panel-tipo"
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_TIPO ? "apf-form-tab--active" : ""
                      }`}
                      onClick={() => setFormTab(FORM_TAB_TIPO)}
                    >
                      <FaUserCog
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Tipo de usuário
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-seguranca"
                      aria-selected={formTab === FORM_TAB_SEGURANCA}
                      aria-controls="acu-panel-seguranca"
                      disabled={!id}
                      title={
                        !id
                          ? "Salve o usuário para gerenciar a senha."
                          : undefined
                      }
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_SEGURANCA
                          ? "apf-form-tab--active"
                          : ""
                      } ${!id ? "apf-form-tab--disabled" : ""}`}
                      onClick={() => id && setFormTab(FORM_TAB_SEGURANCA)}
                    >
                      <FaKey
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Segurança
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-exclusao"
                      aria-selected={formTab === FORM_TAB_EXCLUSAO}
                      aria-controls="acu-panel-exclusao"
                      disabled={!id}
                      title={
                        !id
                          ? "Salve o usuário para excluir o cadastro."
                          : undefined
                      }
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_EXCLUSAO
                          ? "apf-form-tab--active"
                          : ""
                      } ${!id ? "apf-form-tab--disabled" : ""} apf-form-tab--danger`}
                      onClick={() => id && setFormTab(FORM_TAB_EXCLUSAO)}
                    >
                      <FaTrashAlt
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Excluir usuário
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-conteudo"
                      aria-selected={formTab === FORM_TAB_CONTEUDO}
                      aria-controls="acu-panel-conteudo"
                      disabled={!id}
                      title={
                        !id
                          ? "Salve o usuário para visualizar os conteúdos."
                          : undefined
                      }
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_CONTEUDO
                          ? "apf-form-tab--active"
                          : ""
                      } ${!id ? "apf-form-tab--disabled" : ""}`}
                      onClick={() => id && setFormTab(FORM_TAB_CONTEUDO)}
                    >
                      <FaCalendarAlt
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Conteúdos
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-slots"
                      aria-selected={formTab === FORM_TAB_SLOTS}
                      aria-controls="acu-panel-slots"
                      disabled={!id}
                      title={
                        !id
                          ? "Salve o usuário para gerenciar slots bloqueados."
                          : undefined
                      }
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_SLOTS
                          ? "apf-form-tab--active"
                          : ""
                      } ${!id ? "apf-form-tab--disabled" : ""}`}
                      onClick={() => id && setFormTab(FORM_TAB_SLOTS)}
                    >
                      <FaBan
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Slots bloqueados
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="acu-tab-dossie"
                      aria-selected={formTab === FORM_TAB_DOSSIE}
                      aria-controls="acu-panel-dossie"
                      disabled={!id}
                      title={
                        !id
                          ? "Salve o usuário para visualizar o dossiê."
                          : undefined
                      }
                      className={`apf-form-tab ${
                        formTab === FORM_TAB_DOSSIE
                          ? "apf-form-tab--active"
                          : ""
                      } ${!id ? "apf-form-tab--disabled" : ""}`}
                      onClick={() => id && setFormTab(FORM_TAB_DOSSIE)}
                    >
                      <FaFileAlt
                        size={14}
                        className="apf-form-tab-icon"
                        aria-hidden
                      />
                      Dossiê
                    </button>
                  </nav>

                  <div className="apf-form-tabs-panels">
                    {formTab === FORM_TAB_IDENTIDADE && (
                      <div
                        id="acu-panel-identidade"
                        role="tabpanel"
                        aria-labelledby="acu-tab-identidade"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">
                          Identidade e contato
                        </h2>
                        <div className="apf-form-layout">
                          <div className="apf-logo-card">
                            <p className="apf-logo-label">Foto de perfil</p>
                            <div
                              className={`apf-upload-zone ${
                                avatarDragActive
                                  ? "apf-upload-zone--active"
                                  : ""
                              } ${avatarProcessing ? "apf-upload-zone--busy" : ""}`}
                              onDragEnter={onAvatarDragEnter}
                              onDragLeave={onAvatarDragLeave}
                              onDragOver={onAvatarDragOver}
                              onDrop={onAvatarDrop}
                              onClick={avatarProcessing ? undefined : openAvatarPicker}
                              onKeyDown={onAvatarZoneKeyDown}
                              role="button"
                              tabIndex={0}
                              aria-label="Enviar foto de perfil"
                            >
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="visually-hidden"
                                accept="image/*"
                                onChange={handleAvatarInputChange}
                                tabIndex={-1}
                              />
                              {avatarExibir ? (
                                <div className="apf-upload-preview apf-upload-preview--avatar">
                                  <img
                                    src={avatarExibir}
                                    alt=""
                                    className="apf-upload-preview-img apf-avatar-preview-round"
                                  />
                                  <div className="apf-upload-preview-overlay">
                                    {avatarProcessing ? (
                                      <>
                                        <FaSpinner
                                          className="fa-spin"
                                          size={20}
                                          aria-hidden
                                        />
                                        <span>Processando…</span>
                                      </>
                                    ) : (
                                      <>
                                        <FaImage size={20} aria-hidden />
                                        <span>Substituir imagem</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="apf-upload-empty">
                                  <span
                                    className="apf-upload-icon-wrap"
                                    aria-hidden
                                  >
                                    <FaCloudUploadAlt size={36} />
                                  </span>
                                  <p className="apf-upload-title">
                                    Arraste o arquivo ou clique para explorar
                                  </p>
                                  <p className="apf-upload-sub">
                                    PNG, JPG ou WebP · até 15 MB
                                  </p>
                                  <span className="apf-upload-browse">
                                    Escolher arquivo
                                  </span>
                                </div>
                              )}
                            </div>
                            {id && temAvatarReal ? (
                              <button
                                type="button"
                                className="apl-btn apl-btn--ghost apf-avatar-edit-btn"
                                onClick={openAvatarCropExisting}
                                disabled={avatarProcessing}
                              >
                                {avatarProcessing ? (
                                  <FaSpinner
                                    className="fa-spin"
                                    size={14}
                                    aria-hidden
                                  />
                                ) : (
                                  <FaCrop size={14} aria-hidden />
                                )}
                                Ajustar foto atual
                              </button>
                            ) : null}
                            <p className="apf-logo-hint">
                              {id
                                ? "Opcional. Use “Ajustar foto atual” para editar o enquadramento da imagem já salva, ou envie uma nova. Salve o usuário para substituir o avatar."
                                : "Opcional. Recorte central quadrado automático, sem distorcer a proporção. Exibida no perfil e nas listagens."}
                            </p>
                          </div>

                          <div className="apf-controls w-100">
                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Nome completo"
                                  type="text"
                                  requered
                                  value={nomeCompleto}
                                  onChange={setNomeCompleto}
                                />
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Nome para o crachá"
                                  type="text"
                                  requered
                                  value={nomeCracha}
                                  onChange={setNomeCracha}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-bio"
                                >
                                  Bio
                                </label>
                                <textarea
                                  id="acu-bio"
                                  className="form-control form-control-lg"
                                  rows="4"
                                  placeholder="Breve apresentação do participante…"
                                  value={bio || ""}
                                  onChange={(e) => setBio(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="CPF"
                                  type="text"
                                  requered
                                  mascara="999.999.999-99"
                                  countCPF
                                  value={cpf}
                                  onChange={setCPF}
                                />
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="E-mail"
                                  type="email"
                                  requered
                                  value={email}
                                  onChange={setEmail}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-dt-nasc"
                                >
                                  Data de nascimento{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  id="acu-dt-nasc"
                                  type="date"
                                  className="form-control form-control-lg"
                                  value={dataNascimento || ""}
                                  onChange={(e) =>
                                    setDataNascimento(e.target.value)
                                  }
                                />
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <InputRadio
                                  label="Sexo"
                                  values={["Masculino", "Feminino"]}
                                  onChange={setSexo}
                                  value={sexo}
                                  requered
                                  validador={() => {}}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Telefone comercial"
                                  type="tel"
                                  requered
                                  mascara="(99) 99999-9999"
                                  value={telComercial}
                                  onChange={setTelComercial}
                                />
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Celular"
                                  type="tel"
                                  requered
                                  mascara="(99) 99999-9999"
                                  value={celular}
                                  onChange={setCelular}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label label-input" htmlFor="acu-estado">
                                  Estado <span className="text-danger">*</span>
                                </label>
                                <select
                                  id="acu-estado"
                                  className="form-control form-control-lg"
                                  value={estado || ""}
                                  onChange={(e) => {
                                    const nextEstado = e.target.value;
                                    setEstado(nextEstado);
                                    setRegiao(regiaoPorEstado(nextEstado) || "");
                                  }}
                                >
                                  <option value="">Selecione uma opção</option>
                                  {ESTADOS_BR.map((nome) => (
                                    <option key={nome} value={nome}>
                                      {nome}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label label-input" htmlFor="acu-regiao">
                                  Região <span className="text-danger">*</span>
                                </label>
                                <select
                                  id="acu-regiao"
                                  className="form-control form-control-lg"
                                  value={regiao || ""}
                                  disabled={!estado}
                                >
                                  <option value="">
                                    {estado ? "Região" : "Selecione o estado primeiro"}
                                  </option>
                                  {REGIOES_BR.map((nome) => (
                                    <option key={nome} value={nome}>
                                      {nome}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Nome da instituição"
                                  type="text"
                                  requered
                                  value={nomeInstituicao}
                                  onChange={setnomeInstituicao}
                                />
                              </div>
                              <div className="col-lg-6 col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Cargo"
                                  type="text"
                                  requered
                                  value={cargo}
                                  onChange={setCargo}
                                />
                              </div>
                            </div>

                            <h3 className="apf-section-title mt-2 mb-3">
                              Dados do dossiê
                            </h3>
                            <p className="form-text text-muted mb-3">
                              Campos exibidos na folha do dossiê do convidado.
                            </p>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-segmento"
                                >
                                  Segmento
                                </label>
                                <select
                                  id="acu-segmento"
                                  className="form-control form-control-lg"
                                  value={segmento || ""}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setSegmento(next);
                                    if (next !== "outros") {
                                      setSegmentoOutros("");
                                    }
                                  }}
                                >
                                  {opcoesSegmento.map(([v, lab]) => (
                                    <option key={v || "seg-empty"} value={v}>
                                      {lab}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {segmento === "outros" ? (
                                <div className="col-lg-6 col-12 mb-3">
                                  <InputTextPatrocinador
                                    label="Descrição do segmento"
                                    type="text"
                                    value={segmentoOutros}
                                    onChange={setSegmentoOutros}
                                  />
                                </div>
                              ) : null}
                              <div className="col-lg-6 col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-faturamento"
                                >
                                  Faturamento estimado 2025
                                </label>
                                <select
                                  id="acu-faturamento"
                                  className="form-control form-control-lg"
                                  value={faturamento || ""}
                                  onChange={(e) =>
                                    setfaturamento(e.target.value)
                                  }
                                >
                                  {opcoesFaturamento.map(([v, lab]) => (
                                    <option key={v || "fat-empty"} value={v}>
                                      {lab}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 mb-3">
                                <InputTextPatrocinador
                                  label="Prioridade de Investimento 26/27"
                                  type="text"
                                  value={prioridade}
                                  onChange={setPrioridade}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-transformacao"
                                >
                                  Qual o maior desafio da sua instituição no
                                  momento?
                                </label>
                                <textarea
                                  id="acu-transformacao"
                                  className="form-control form-control-lg"
                                  rows="3"
                                  placeholder="Descreva o principal desafio…"
                                  value={transformacao || ""}
                                  onChange={(e) =>
                                    setTransformacao(e.target.value)
                                  }
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-procura"
                                >
                                  O que você procura SBF?
                                </label>
                                <select
                                  id="acu-procura"
                                  className="form-control form-control-lg"
                                  value={procura || ""}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setprocura(next);
                                    if (next !== "outros") {
                                      setOutrosProcura("");
                                    }
                                  }}
                                >
                                  {opcoesProcura.map(([v, lab]) => (
                                    <option key={v || "proc-empty"} value={v}>
                                      {lab}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {procura === "outros" ? (
                                <div className="col-lg-6 col-12 mb-3">
                                  <InputTextPatrocinador
                                    label="Descrição do que procura"
                                    type="text"
                                    value={outrosProcura}
                                    onChange={setOutrosProcura}
                                  />
                                </div>
                              ) : null}
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12 mb-3">
                                <label
                                  className="form-label label-input"
                                  htmlFor="acu-hobby"
                                >
                                  Atividades de Interesse
                                </label>
                                <select
                                  id="acu-hobby"
                                  className="form-control form-control-lg"
                                  value={hobbyAtividade || ""}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    atualizarHobby(
                                      next,
                                      next === "outros" ? hobbyOutros : "",
                                    );
                                  }}
                                >
                                  {opcoesHobby.map(
                                    ([v, lab]) => (
                                      <option
                                        key={v || "hobby-empty"}
                                        value={v}
                                      >
                                        {lab}
                                      </option>
                                    ),
                                  )}
                                </select>
                              </div>
                              {hobbyAtividade === "outros" ? (
                                <div className="col-lg-6 col-12 mb-3">
                                  <InputTextPatrocinador
                                    label="Descrição da atividade"
                                    type="text"
                                    value={hobbyOutros}
                                    onChange={(valor) =>
                                      atualizarHobby("outros", valor)
                                    }
                                  />
                                </div>
                              ) : null}
                            </div>

                            {showAlerta ? (
                              <div className="apl-alert" role="alert">
                                Um ou mais campos obrigatórios não foram
                                preenchidos corretamente.
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )}

                    {formTab === FORM_TAB_TIPO && (
                      <div
                        id="acu-panel-tipo"
                        role="tabpanel"
                        aria-labelledby="acu-tab-tipo"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">Tipo de usuário</h2>
                        <div className="apf-controls acu-tipo-panel">
                          <div className="row">
                            <div className="col-lg-6 col-12 mb-3">
                              <label
                                className="form-label label-input"
                                htmlFor="acu-tipo-usuario"
                              >
                                Tipo{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                id="acu-tipo-usuario"
                                className="form-select form-control form-control-lg"
                                value={tipo || ""}
                                onChange={(e) => setTipo(e.target.value)}
                                aria-label="Tipo de usuário"
                              >
                                <option value="">Selecione o tipo</option>
                                {OPCOES_TIPO_USUARIO.map((op) => (
                                  <option key={op.value} value={op.value}>
                                    {op.label}
                                  </option>
                                ))}
                              </select>
                              <p className="form-text small text-muted mb-0 mt-1">
                                Define permissões e telas no sistema. Para criar
                                a conta portal de uma instituição nova, use
                                Patrocinadores → criar usuário.
                              </p>
                            </div>
                          </div>
                          {tipoSelecionado ? (
                            <p className="acu-tipo-descricao">
                              {tipoSelecionado.desc}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {formTab === FORM_TAB_SEGURANCA && id && (
                      <div
                        id="acu-panel-seguranca"
                        role="tabpanel"
                        aria-labelledby="acu-tab-seguranca"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">Segurança</h2>
                        <div className="acu-seguranca-panel">
                          <p className="acu-seguranca-lead">
                            Envia um e-mail ao usuário com link para definir uma
                            nova senha (mesmo fluxo do cadastro). O link expira
                            em 48 horas e também fica disponível abaixo para
                            copiar e compartilhar.
                          </p>
                          <button
                            type="button"
                            className="apl-btn apl-btn--secondary"
                            onClick={abrirConfirmacaoRedefinirSenha}
                            disabled={isRedefinindoSenha || !email}
                            title={
                              !email
                                ? "Usuário sem e-mail cadastrado"
                                : undefined
                            }
                          >
                            {isRedefinindoSenha ? (
                              <FaSpinner
                                className="fa-spin"
                                size={14}
                                aria-hidden
                              />
                            ) : (
                              <FaPaperPlane size={14} aria-hidden />
                            )}
                            {isRedefinindoSenha
                              ? "Enviando…"
                              : "Redefinir senha"}
                          </button>

                          <div className="acu-seguranca-link-block">
                            <label
                              className="acu-seguranca-link-label"
                              htmlFor="acu-link-alterar-senha"
                            >
                              <FaLink size={13} aria-hidden />
                              Link de alteração de senha
                            </label>
                            {linkAlterarSenha ? (
                              <div className="acu-seguranca-link-row">
                                <input
                                  id="acu-link-alterar-senha"
                                  type="text"
                                  className="apl-search-input acu-seguranca-link-input"
                                  value={linkAlterarSenha}
                                  readOnly
                                  onFocus={(e) => e.target.select()}
                                  aria-label="Link de alteração de senha"
                                />
                                <button
                                  type="button"
                                  className="apl-btn apl-btn--ghost"
                                  onClick={handleCopiarLinkAlterarSenha}
                                  title="Copiar link"
                                >
                                  <FaCopy size={13} aria-hidden />
                                  Copiar
                                </button>
                                <a
                                  className="apl-btn apl-btn--ghost"
                                  href={linkAlterarSenha}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Abrir link em nova aba"
                                >
                                  Abrir
                                </a>
                              </div>
                            ) : (
                              <p className="acu-seguranca-link-empty">
                                Clique em <strong>Redefinir senha</strong> para
                                gerar o link e enviá-lo por e-mail. O endereço
                                aparecerá aqui para cópia.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {formTab === FORM_TAB_EXCLUSAO && id && (
                      <div
                        id="acu-panel-exclusao"
                        role="tabpanel"
                        aria-labelledby="acu-tab-exclusao"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">Excluir usuário</h2>
                        <div className="acu-exclusao-panel">
                          <div
                            className="acu-exclusao-alert"
                            role="alert"
                            aria-live="polite"
                          >
                            <FaExclamationTriangle
                              size={20}
                              className="acu-exclusao-alert-icon"
                              aria-hidden
                            />
                            <div>
                              <p className="acu-exclusao-alert-title mb-1">
                                Exclusão definitiva
                              </p>
                              <p className="acu-exclusao-alert-text mb-0">
                                Ao excluir, o cadastro de{" "}
                                <strong>{nomeCompleto || "este usuário"}</strong>{" "}
                                será removido do sistema. O acesso ao portal será
                                revogado e o usuário não aparecerá mais nas
                                listagens administrativas.
                              </p>
                            </div>
                          </div>
                          <p className="acu-exclusao-lead">
                            Esta operação não pode ser desfeita pela interface.
                            Confirme apenas se tiver certeza de que o cadastro
                            deve ser encerrado.
                          </p>
                          <button
                            type="button"
                            className="apl-btn acu-btn--danger"
                            onClick={abrirConfirmacaoExcluirUsuario}
                            disabled={isExcluindoUsuario}
                          >
                            {isExcluindoUsuario ? (
                              <FaSpinner
                                className="fa-spin"
                                size={14}
                                aria-hidden
                              />
                            ) : (
                              <FaTrashAlt size={14} aria-hidden />
                            )}
                            {isExcluindoUsuario
                              ? "Excluindo…"
                              : "Excluir usuário"}
                          </button>
                        </div>
                      </div>
                    )}

                    {formTab === FORM_TAB_CONTEUDO && id && (
                      <div
                        id="acu-panel-conteudo"
                        role="tabpanel"
                        aria-labelledby="acu-tab-conteudo"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">
                          Agenda de conteúdos
                        </h2>
                        <p className="acu-conteudo-lead">
                          Gerencie a ação especial, os conteúdos do
                          credenciamento e a inscrição em agenda extraordinária.
                        </p>

                        <section
                          className="acu-gestao-bloco"
                          aria-labelledby="acu-conteudos-edit-title"
                        >
                          <h3
                            id="acu-conteudos-edit-title"
                            className="acu-gestao-title"
                          >
                            Conteúdos escolhidos
                          </h3>
                          <p className="acu-conteudo-lead">
                            Inscreva ou retire o convidado nos conteúdos
                            editoriais e patrocinados (até 2 slots).
                          </p>
                          {opcoesAgendaLoading ? (
                            <p className="acu-conteudo-empty">
                              <FaSpinner className="fa-spin" aria-hidden />{" "}
                              Carregando agendas…
                            </p>
                          ) : (
                            <ul className="acu-conteudo-lista acu-conteudo-lista--edit">
                              {[
                                {
                                  slot: 1,
                                  value: editConteudo1,
                                  setValue: setEditConteudo1,
                                  other: editConteudo2,
                                },
                                {
                                  slot: 2,
                                  value: editConteudo2,
                                  setValue: setEditConteudo2,
                                  other: editConteudo1,
                                },
                              ].map((slotCfg) => {
                                const idSelecionado = toAgendaId(slotCfg.value);
                                const daLista = idSelecionado
                                  ? opcoesConteudo.find(
                                      (o) =>
                                        Number(o.id) === idSelecionado,
                                    )
                                  : null;
                                const daAgenda = idSelecionado
                                  ? (convidado?.agendaParticipante || [])
                                      .map((ap) => ap?.agenda)
                                      .find(
                                        (a) =>
                                          a && Number(a.id) === idSelecionado,
                                      )
                                  : null;
                                const selecionado = daLista || daAgenda;
                                const rawInicio =
                                  selecionado?.dt_hora_inicio ||
                                  selecionado?.dtHoraInicio;
                                const rawFim =
                                  selecionado?.dt_hora_fim ||
                                  selecionado?.dtHoraFim;
                                const inicio = rawInicio
                                  ? formatarDiaHora(rawInicio).substring(0, 16)
                                  : null;
                                const fim = rawFim
                                  ? formatarDiaHora(rawFim).substring(11, 16)
                                  : null;
                                const detalhe =
                                  selecionado?.detalhe || null;

                                return (
                                  <li
                                    key={`slot-${slotCfg.slot}`}
                                    className="acu-conteudo-item"
                                  >
                                    <div className="acu-conteudo-item-header">
                                      <span className="acu-conteudo-slot">
                                        Conteúdo {slotCfg.slot}
                                      </span>
                                      {selecionado?.tipo ? (
                                        <span className="acu-conteudo-tipo">
                                          {labelTipoConteudo(selecionado.tipo)}
                                        </span>
                                      ) : null}
                                    </div>

                                    {idSelecionado ? (
                                      <>
                                        <h3 className="acu-conteudo-nome">
                                          {selecionado?.nome ||
                                            `Conteúdo #${idSelecionado}`}
                                        </h3>
                                        <div className="acu-conteudo-meta">
                                          {inicio ? (
                                            <span className="acu-conteudo-meta-item">
                                              <FaClock size={12} aria-hidden />
                                              {fim
                                                ? `${inicio} – ${fim}`
                                                : inicio}
                                            </span>
                                          ) : null}
                                          {selecionado?.local ? (
                                            <span className="acu-conteudo-meta-item">
                                              <FaMapMarkerAlt
                                                size={12}
                                                aria-hidden
                                              />
                                              {selecionado.local}
                                            </span>
                                          ) : null}
                                        </div>
                                        {detalhe ? (
                                          <p className="acu-conteudo-detalhe">
                                            {detalhe}
                                          </p>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="apl-btn apl-btn--ghost acu-btn-retirar"
                                          onClick={() => slotCfg.setValue("")}
                                        >
                                          <FaTrashAlt size={12} aria-hidden />
                                          Retirar inscrição
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <label
                                          className="form-label label-input"
                                          htmlFor={`acu-conteudo-slot-${slotCfg.slot}`}
                                        >
                                          Selecionar agenda
                                        </label>
                                        <select
                                          id={`acu-conteudo-slot-${slotCfg.slot}`}
                                          className="form-select form-control"
                                          value={slotCfg.value}
                                          onChange={(e) =>
                                            slotCfg.setValue(e.target.value)
                                          }
                                        >
                                          <option value="">
                                            Selecione um conteúdo
                                          </option>
                                          {opcoesConteudo.map((opcao) => {
                                            const disabled =
                                              slotCfg.other &&
                                              String(slotCfg.other) ===
                                                String(opcao.id);
                                            return (
                                              <option
                                                key={opcao.id}
                                                value={String(opcao.id)}
                                                disabled={disabled}
                                              >
                                                {labelOpcaoAgenda(opcao)}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      </>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </section>

                        <div className="acu-gestao-row">
                        <section
                          className="acu-gestao-bloco acu-gestao-bloco--half"
                          aria-labelledby="acu-acao-especial-title"
                        >
                          <h3
                            id="acu-acao-especial-title"
                            className="acu-gestao-title"
                          >
                            Ação especial
                          </h3>
                          <p className="acu-conteudo-lead">
                            Escolha registrada no credenciamento (degustação de
                            caviar):
                          </p>
                          <p className="acu-acao-especial-valor">
                            {labelAcaoEspecial(
                              convidado?.acao_especial ??
                                convidado?.acaoEspecial,
                            )}
                          </p>
                        </section>

                        <section
                          className="acu-gestao-bloco acu-gestao-bloco--half"
                          aria-labelledby="acu-extraordinaria-title"
                        >
                          <h3
                            id="acu-extraordinaria-title"
                            className="acu-gestao-title"
                          >
                            Agenda extraordinária
                          </h3>
                          <p className="acu-conteudo-lead">
                            Inscreva ou retire o convidado em um conteúdo do
                            tipo extraordinária.
                          </p>
                          {opcoesAgendaLoading ? (
                            <p className="acu-conteudo-empty">
                              <FaSpinner className="fa-spin" aria-hidden />{" "}
                              Carregando agendas…
                            </p>
                          ) : (
                            (() => {
                              const idExtra = toAgendaId(editExtraordinaria);
                              const daLista = idExtra
                                ? opcoesExtraordinaria.find(
                                    (o) => Number(o.id) === idExtra,
                                  )
                                : null;
                              const daAgenda = idExtra
                                ? (convidado?.agendaParticipante || [])
                                    .map((ap) => ap?.agenda)
                                    .find(
                                      (a) => a && Number(a.id) === idExtra,
                                    )
                                : null;
                              const selecionado = daLista || daAgenda;
                              const rawInicio =
                                selecionado?.dt_hora_inicio ||
                                selecionado?.dtHoraInicio;
                              const rawFim =
                                selecionado?.dt_hora_fim ||
                                selecionado?.dtHoraFim;
                              const inicio = rawInicio
                                ? formatarDiaHora(rawInicio).substring(0, 16)
                                : null;
                              const fim = rawFim
                                ? formatarDiaHora(rawFim).substring(11, 16)
                                : null;
                              const detalhe = selecionado?.detalhe || null;

                              if (idExtra) {
                                return (
                                  <div className="acu-conteudo-item acu-conteudo-item--solo">
                                    <div className="acu-conteudo-item-header">
                                      {selecionado?.tipo ? (
                                        <span className="acu-conteudo-tipo">
                                          {labelTipoConteudo(selecionado.tipo)}
                                        </span>
                                      ) : (
                                        <span className="acu-conteudo-tipo">
                                          Extraordinária
                                        </span>
                                      )}
                                    </div>
                                    <h3 className="acu-conteudo-nome">
                                      {selecionado?.nome ||
                                        `Agenda #${idExtra}`}
                                    </h3>
                                    <div className="acu-conteudo-meta">
                                      {inicio ? (
                                        <span className="acu-conteudo-meta-item">
                                          <FaClock size={12} aria-hidden />
                                          {fim ? `${inicio} – ${fim}` : inicio}
                                        </span>
                                      ) : null}
                                      {selecionado?.local ? (
                                        <span className="acu-conteudo-meta-item">
                                          <FaMapMarkerAlt
                                            size={12}
                                            aria-hidden
                                          />
                                          {selecionado.local}
                                        </span>
                                      ) : null}
                                    </div>
                                    {detalhe ? (
                                      <p className="acu-conteudo-detalhe">
                                        {detalhe}
                                      </p>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="apl-btn apl-btn--ghost acu-btn-retirar"
                                      onClick={() => setEditExtraordinaria("")}
                                    >
                                      <FaTrashAlt size={12} aria-hidden />
                                      Retirar inscrição
                                    </button>
                                  </div>
                                );
                              }

                              return (
                                <div className="acu-extraordinaria-form">
                                  <label
                                    className="form-label label-input"
                                    htmlFor="acu-extraordinaria-select"
                                  >
                                    Selecionar agenda
                                  </label>
                                  <select
                                    id="acu-extraordinaria-select"
                                    className="form-select form-control"
                                    value={editExtraordinaria}
                                    onChange={(e) =>
                                      setEditExtraordinaria(e.target.value)
                                    }
                                  >
                                    <option value="">
                                      Selecione uma agenda
                                    </option>
                                    {opcoesExtraordinaria.map((opcao) => (
                                      <option
                                        key={opcao.id}
                                        value={String(opcao.id)}
                                      >
                                        {labelOpcaoAgenda(opcao)}
                                      </option>
                                    ))}
                                  </select>
                                  {opcoesExtraordinaria.length === 0 ? (
                                    <p className="acu-conteudo-empty">
                                      Nenhuma agenda extraordinária cadastrada.
                                    </p>
                                  ) : null}
                                </div>
                              );
                            })()
                          )}
                        </section>
                        </div>

                        <div className="acu-gestao-actions">
                          <button
                            type="button"
                            className="apl-btn apl-btn--primary"
                            onClick={handleSalvarAgendaConteudo}
                            disabled={
                              salvandoAgendaConteudo || opcoesAgendaLoading
                            }
                          >
                            {salvandoAgendaConteudo ? (
                              <FaSpinner
                                className="fa-spin"
                                size={14}
                                aria-hidden
                              />
                            ) : (
                              <FaSave size={14} aria-hidden />
                            )}
                            Salvar agenda de conteúdos
                          </button>
                        </div>

                        <div className="acu-conteudo-link-block">
                          <button
                            type="button"
                            className="acu-conteudo-link-toggle"
                            aria-expanded={linkConteudoAberto}
                            aria-controls="acu-conteudo-link-panel"
                            id="acu-conteudo-link-toggle"
                            onClick={() =>
                              setLinkConteudoAberto((aberto) => !aberto)
                            }
                          >
                            <span className="acu-conteudo-link-title">
                              <FaLink size={14} aria-hidden /> Link de escolha
                              de conteúdos
                            </span>
                            <span className="acu-conteudo-link-toggle-meta">
                              {!tokenConteudoLoading ? (
                                <strong
                                  className={`acu-token-status acu-token-status--${
                                    tokenConteudo?.status || "inexistente"
                                  }`}
                                >
                                  {labelStatusTokenConteudo(
                                    tokenConteudo?.status,
                                  )}
                                </strong>
                              ) : null}
                              <FaChevronDown
                                size={14}
                                className={`acu-conteudo-link-chevron${
                                  linkConteudoAberto
                                    ? " acu-conteudo-link-chevron--open"
                                    : ""
                                }`}
                                aria-hidden
                              />
                            </span>
                          </button>
                          {linkConteudoAberto ? (
                            <div
                              id="acu-conteudo-link-panel"
                              role="region"
                              aria-labelledby="acu-conteudo-link-toggle"
                              className="acu-conteudo-link-panel"
                            >
                              <p className="acu-conteudo-lead">
                                Gere um link de uso único para o convidado
                                escolher os conteúdos. O link deixa de valer
                                após a confirmação das escolhas.
                              </p>
                              {tokenConteudoLoading ? (
                                <p className="acu-conteudo-empty">
                                  <FaSpinner
                                    className="fa-spin"
                                    aria-hidden
                                  />{" "}
                                  Carregando link…
                                </p>
                              ) : (
                                <>
                                  <div className="acu-conteudo-link-status">
                                    Status:{" "}
                                    <strong
                                      className={`acu-token-status acu-token-status--${
                                        tokenConteudo?.status || "inexistente"
                                      }`}
                                    >
                                      {labelStatusTokenConteudo(
                                        tokenConteudo?.status,
                                      )}
                                    </strong>
                                  </div>
                                  {tokenConteudo?.url ? (
                                    <div className="acu-conteudo-link-url-row">
                                      <input
                                        type="text"
                                        className="acu-conteudo-link-input"
                                        readOnly
                                        value={tokenConteudo.url}
                                        aria-label="URL do link de conteúdos"
                                      />
                                      <button
                                        type="button"
                                        className="apl-btn apl-btn--ghost"
                                        onClick={handleCopiarLinkConteudo}
                                      >
                                        <FaCopy size={13} aria-hidden />
                                        Copiar
                                      </button>
                                    </div>
                                  ) : (
                                    <p className="acu-conteudo-empty">
                                      Nenhum link gerado ainda.
                                    </p>
                                  )}
                                  {tokenConteudo?.expires_at ? (
                                    <p className="acu-conteudo-link-meta">
                                      Expira em:{" "}
                                      {formatarDiaHora(
                                        tokenConteudo.expires_at,
                                      )}
                                      {tokenConteudo.used_at
                                        ? ` · Usado em: ${formatarDiaHora(
                                            tokenConteudo.used_at,
                                          )}`
                                        : ""}
                                    </p>
                                  ) : null}
                                  <div className="acu-conteudo-link-actions">
                                    <button
                                      type="button"
                                      className="apl-btn apl-btn--primary"
                                      onClick={handleGerarTokenConteudo}
                                      disabled={tokenConteudoGerando}
                                    >
                                      {tokenConteudoGerando ? (
                                        <FaSpinner
                                          className="fa-spin"
                                          size={14}
                                          aria-hidden
                                        />
                                      ) : (
                                        <FaLink size={14} aria-hidden />
                                      )}
                                      {tokenConteudo?.url
                                        ? "Regenerar link"
                                        : "Gerar link"}
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {formTab === FORM_TAB_SLOTS && id && (
                      <div
                        id="acu-panel-slots"
                        role="tabpanel"
                        aria-labelledby="acu-tab-slots"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">Slots bloqueados</h2>
                        <p className="acu-conteudo-lead">
                          Informe a chegada e a partida do convidado no evento.
                          O sistema calcula automaticamente quais horários
                          ficam indisponíveis fora dessa janela.
                        </p>

                        {slotsBloqueadosLoading || slotsCatalogoLoading ? (
                          <p className="acu-conteudo-empty">
                            <FaSpinner className="fa-spin" aria-hidden />{" "}
                            Carregando disponibilidade…
                          </p>
                        ) : (
                          <>
                            <section
                              className="acu-gestao-bloco"
                              aria-labelledby="acu-slots-presenca-title"
                            >
                              <h3
                                id="acu-slots-presenca-title"
                                className="acu-gestao-title"
                              >
                                Janela de presença
                              </h3>
                              <p className="acu-conteudo-lead">
                                Escolha o dia entre as datas do evento e informe
                                apenas a hora de chegada e de partida.
                              </p>
                              <div className="acu-slots-presenca-grid">
                                <div className="acu-slots-presenca-field">
                                  <label
                                    className="form-label label-input"
                                    htmlFor="acu-dia-chegada"
                                  >
                                    Dia de chegada
                                  </label>
                                  <select
                                    id="acu-dia-chegada"
                                    className="form-select form-control form-control-lg"
                                    value={diaChegada}
                                    onChange={(e) =>
                                      setDiaChegada(e.target.value)
                                    }
                                  >
                                    <option value="">Selecione o dia</option>
                                    {diasEventoSlots.map((item) => (
                                      <option key={item.data} value={item.data}>
                                        {labelDiaEvento(item)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="acu-slots-presenca-field">
                                  <label
                                    className="form-label label-input"
                                    htmlFor="acu-hora-chegada"
                                  >
                                    Hora de chegada
                                  </label>
                                  <input
                                    type="time"
                                    id="acu-hora-chegada"
                                    className="form-control form-control-lg"
                                    value={horaChegada}
                                    onChange={(e) =>
                                      setHoraChegada(e.target.value)
                                    }
                                  />
                                </div>
                                <div className="acu-slots-presenca-field">
                                  <label
                                    className="form-label label-input"
                                    htmlFor="acu-dia-partida"
                                  >
                                    Dia de partida
                                  </label>
                                  <select
                                    id="acu-dia-partida"
                                    className="form-select form-control form-control-lg"
                                    value={diaPartida}
                                    onChange={(e) =>
                                      setDiaPartida(e.target.value)
                                    }
                                  >
                                    <option value="">Selecione o dia</option>
                                    {diasEventoSlots.map((item) => (
                                      <option key={item.data} value={item.data}>
                                        {labelDiaEvento(item)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="acu-slots-presenca-field">
                                  <label
                                    className="form-label label-input"
                                    htmlFor="acu-hora-partida"
                                  >
                                    Hora de partida
                                  </label>
                                  <input
                                    type="time"
                                    id="acu-hora-partida"
                                    className="form-control form-control-lg"
                                    value={horaPartida}
                                    onChange={(e) =>
                                      setHoraPartida(e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              {diasEventoSlots.length === 0 ? (
                                <p className="small text-muted mt-2 mb-0">
                                  Nenhuma data de evento ativa ou em
                                  desenvolvimento.
                                </p>
                              ) : null}
                            </section>

                            <div className="acu-gestao-row acu-slots-resumo-row">
                              <section
                                className="acu-gestao-bloco acu-gestao-bloco--half"
                                aria-labelledby="acu-slots-bloqueados-title"
                              >
                                <h3
                                  id="acu-slots-bloqueados-title"
                                  className="acu-gestao-title"
                                >
                                  Indisponíveis (
                                  {slotsBloqueadosPreview.length})
                                </h3>
                                {slotsBloqueadosPreview.length === 0 ? (
                                  <p className="acu-conteudo-empty">
                                    {temPreviewSlots
                                      ? "Nenhum slot bloqueado para esta janela."
                                      : "Informe a chegada ou a partida para calcular."}
                                  </p>
                                ) : (
                                  <ul className="acu-slots-lista">
                                    {slotsBloqueadosPreview.map((slot) => (
                                      <li
                                        key={`blk-${slot.id}`}
                                        className="acu-slots-item acu-slots-item--bloqueado"
                                      >
                                        <span className="acu-slots-item-hora">
                                          <FaClock size={12} aria-hidden />
                                          {formatarSlotResumo(slot)}
                                        </span>
                                        <span className="acu-slots-item-motivo">
                                          {labelMotivoSlot(slot.motivo)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </section>

                              <section
                                className="acu-gestao-bloco acu-gestao-bloco--half"
                                aria-labelledby="acu-slots-disponiveis-title"
                              >
                                <h3
                                  id="acu-slots-disponiveis-title"
                                  className="acu-gestao-title"
                                >
                                  Disponíveis (
                                  {temPreviewSlots
                                    ? slotsDisponiveisPreview.length
                                    : "—"}
                                  )
                                </h3>
                                {!temPreviewSlots ? (
                                  <p className="acu-conteudo-empty">
                                    Informe a chegada ou a partida para calcular.
                                  </p>
                                ) : slotsDisponiveisPreview.length === 0 ? (
                                  <p className="acu-conteudo-empty">
                                    Nenhum slot disponível nesta janela.
                                  </p>
                                ) : (
                                  <ul className="acu-slots-lista">
                                    {slotsDisponiveisPreview.map((slot) => (
                                      <li
                                        key={`ok-${slot.id}`}
                                        className="acu-slots-item acu-slots-item--ok"
                                      >
                                        <span className="acu-slots-item-hora">
                                          <FaClock size={12} aria-hidden />
                                          {formatarSlotResumo(slot)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </section>
                            </div>

                            <div className="acu-gestao-actions">
                              <button
                                type="button"
                                className="apl-btn apl-btn--primary"
                                onClick={handleSalvarSlotsBloqueados}
                                disabled={salvandoSlotsBloqueados}
                              >
                                {salvandoSlotsBloqueados ? (
                                  <FaSpinner
                                    className="fa-spin"
                                    size={14}
                                    aria-hidden
                                  />
                                ) : (
                                  <FaSave size={14} aria-hidden />
                                )}
                                Salvar slots bloqueados
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {formTab === FORM_TAB_DOSSIE && id && (
                      <div
                        id="acu-panel-dossie"
                        role="tabpanel"
                        aria-labelledby="acu-tab-dossie"
                        className="apf-form-tabpanel"
                      >
                        <h2 className="apf-section-title">
                          Dossiê do convidado
                        </h2>
                        {convidadoDossiePreview ? (
                          <div className="acu-dossie-preview">
                            <DossieConvidadoSheet
                              convidado={convidadoDossiePreview}
                              embedded
                            />
                          </div>
                        ) : (
                          <p className="acu-dossie-lead">
                            Carregando dados do dossiê…
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="apf-form-actions">
                  <button
                    type="button"
                    className="apl-btn apl-btn--primary"
                    onClick={handleSalvar}
                    disabled={isSaving || isLoading}
                  >
                    {isSaving ? (
                      <FaSpinner className="fa-spin" size={14} aria-hidden />
                    ) : (
                      <FaSave size={14} aria-hidden />
                    )}
                    {isSaving ? "Salvando…" : "Salvar"}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdmFormConvidados;
